import React, { useState, useEffect, useMemo } from 'react';
import {
  useNavigate,
  useSearchParams,
  useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { bandasAPI,
  cofradiasAPI,
  avalesAPI } from '../services/api';
import { getCofradiaPlaceholderIcon } from '../utils/cofradiaPlaceholderIcon';
import { formatFiestaLocalRango } from '../utils/fiestaLocalDisplay';
import AppLayout from './AppLayout.jsx';
import ShareProfileButton from './ShareProfileButton.jsx';
import { buildPublicProfileShareUrl } from '../utils/publicProfileShareUrl.js';
import { ProfileBadges,
  AvalCountBadge,
  ReviewRatingBadge } from './AvalBadge.jsx';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  Image,
  Icon,
  SimpleGrid,
  Card,
  Alert,
  Flex,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  List,
  Link,
  Separator,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaUsers,
  FaCalendarAlt,
  FaMusic,
  FaChurch,
  FaCheckCircle,
  FaArrowLeft,
  FaHeart,
  FaShieldAlt,
  FaPlay,
  FaStar,
  FaRegStar,
  FaClock,
  FaHandshake,
  FaAward,
  FaGavel,
  FaHistory,
  FaBuilding,
  FaTiktok,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

const normalizeWebsiteHref = (raw) => {
  if (!raw || !String(raw).trim()) return '#';
  const t = String(raw).trim();
  if (t.startsWith('http://') || t.startsWith('https://')) return t;
  return `https://${t.replace(/^\/+/, '')}`;
};

// Función para generar URLs de redes sociales
const getSocialUrl = (platform, value) => {
  if (!value) return null;
  
  // Si ya es una URL completa, devolverla
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }
  
  // Limpiar @ del inicio si existe
  let cleanValue = value.startsWith('@') ? value.substring(1) : value;
  
  // Limpiar prefijos de dominio si el usuario los puso sin protocolo (ej: www.facebook.com/usuario)
  cleanValue = cleanValue.replace(/^(www\.)?(instagram\.com|facebook\.com|youtube\.com|youtu\.be|tiktok\.com|x\.com|twitter\.com)\/?/i, '');
  
  switch (platform) {
    case 'instagram':
      return `https://instagram.com/${cleanValue}`;
    case 'facebook':
      return `https://facebook.com/${cleanValue}`;
    case 'youtube':
      // Si tiene @ al principio, es un handle de canal
      if (value.startsWith('@')) {
        return `https://youtube.com/@${cleanValue}`;
      }
      // Si parece una ruta de canal
      if (cleanValue.startsWith('c/') || cleanValue.startsWith('channel/') || cleanValue.startsWith('user/')) {
        return `https://youtube.com/${cleanValue}`;
      }
      // Si es solo un nombre, asumir que es un handle de canal
      return `https://youtube.com/@${cleanValue}`;
    case 'twitter':
      return `https://x.com/${cleanValue}`;
    case 'tiktok':
      return `https://www.tiktok.com/@${cleanValue}`;
    default:
      return null;
  }
};

const ProfileDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, user, isAuthenticated } = useAuth();

  const isBandaPath = location.pathname === '/ver-perfil-banda';
  const isGuestPublicProfileView =
    !isAuthenticated && (isBandaPath || location.pathname === '/ver-perfil-cofradia');
  const appHeaderVariant = isGuestPublicProfileView ? 'guestPublic' : 'default';
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id'); // UUID string
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  const publicShareUrl = useMemo(() => {
    const path = location.pathname;
    const isPublicProfilePath = path === '/ver-perfil-banda' || path === '/ver-perfil-cofradia';

    const queryFromRouter = (id ?? new URLSearchParams(location.search).get('id') ?? '').trim();
    const queryFromWindow =
      typeof window !== 'undefined'
        ? (new URL(window.location.href).searchParams.get('id') ?? '').trim()
        : '';
    const queryId = queryFromRouter || queryFromWindow;

    const profileIdRaw = profileData?.id;
    const profileId =
      profileIdRaw != null && String(profileIdRaw).trim() !== '' ? String(profileIdRaw).trim() : '';

    // '' con ?? no cae al queryId; priorizar id de URL y perfil con trim (evita shareUrl vacío).
    const resolvedId = profileId || queryId;
    if (!resolvedId) return '';

    if (isPublicProfilePath && typeof window !== 'undefined') {
      const origin = window.location.origin;
      return `${origin}${path}?id=${encodeURIComponent(resolvedId)}`;
    }

    const entityIsBanda = profileData ? profileData.type === 'banda' : isBandaPath;
    return buildPublicProfileShareUrl(entityIsBanda ? 'banda' : 'cofradia', resolvedId);
  }, [profileData?.id, profileData?.type, id, isBandaPath, location.pathname, location.search]);
  
  // Estado para el modal de imagen
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
    setIsImageModalOpen(true);
  };
  
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  // Función para extraer ID de video de YouTube
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }
    return null;
  };
  const isYouTubeShort = (url) => !!(url && /youtube\.com\/shorts\//.test(url));

  useEffect(() => {
    const loadProfile = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setProfileData(null);
      
      // Ruta fija: no usar includes('banda') (falsos positivos con otras URL).
      const isBanda = isBandaPath;

      const fetchPerfilPublico = async () => {
        if (isBanda) {
          return bandasAPI.getById(id);
        }
        return cofradiasAPI.getById(id);
      };
      
      try {
        let response;
        try {
          response = await fetchPerfilPublico();
        } catch (err) {
          if (err?.response?.status === 404) {
            /* Mismo id pero entidad equivocada: p. ej. id de User o de Cofradía en URL de banda (y al revés). */
            if (isBanda) {
              const alt = await cofradiasAPI.getById(id).catch(() => null);
              if (alt?.data) {
                navigate(
                  `/ver-perfil-cofradia?id=${encodeURIComponent(id)}`,
                  { replace: true },
                );
                return;
              }
            } else {
              const alt = await bandasAPI.getById(id).catch(() => null);
              if (alt?.data) {
                navigate(
                  `/ver-perfil-banda?id=${encodeURIComponent(id)}`,
                  { replace: true },
                );
                return;
              }
            }
          }
          // Enlace antiguo: ?id= userId con sesión; perfil "mi" entidad
          if (err?.response?.status === 404 && isAuthenticated && id === user?.id) {
            if (isBanda && user?.userType === 'BANDA') {
              const me = await bandasAPI.getMyProfile();
              if (me.data?.id) {
                response = await bandasAPI.getById(me.data.id);
              } else {
                throw err;
              }
            } else if (!isBanda && user?.userType === 'COFRADIA') {
              const me = await cofradiasAPI.getMyProfile();
              if (me.data?.id) {
                response = await cofradiasAPI.getById(me.data.id);
              } else {
                throw err;
              }
            } else {
              throw err;
            }
          } else {
            throw err;
          }
        }
        
        if (response.data) {
          // Transformar datos de la API al formato esperado
          const data = response.data;
          const publicEntityId = data.id;
          console.log('🎵 Datos del repertorio recibido:', data.repertorio);
          setProfileData({
            id: data.id,
            name: data.nombre || 'Sin nombre',
            type: isBanda ? 'banda' : 'cofradia',
            location: data.localizacion?.ciudad || 'Sin ubicación',
            province: data.localizacion?.provincia || '',
            description: data.bio || '',
            longDescription: data.bio || '',
            image: data.avatar,
            logo: data.avatar,
            backgroundImage: data.fondoHeaderImagen,
            verified: data.verificationStatus === 'VERIFIED',
            tipoGrupo: data.tipoGrupo || '',
            tipoEntidad: data.tipoEntidad || null,
            perfilVariante:
              data.tipoEntidad?.variantePerfil || 'RELIGIOSO',
            habitantesMunicipio: data.habitantesMunicipio,
            fiestasLocales: data.fiestasLocales || [],
            patronDelPueblo: data.patronDelPueblo,
            patronaDelPueblo: data.patronaDelPueblo,
            culturalAmbito: data.culturalAmbito,
            culturalNumeroSocios: data.culturalNumeroSocios,
            culturalActividadesDescripcion: data.culturalActividadesDescripcion,
            nMusicos: data.nMusicos || 0,
            members: isBanda ? data.nMusicos : data.numeroHermanos,
            foundedYear: isBanda ? data.anoFundacion : data.fechaFundacion,
            especialidades: data.especialidades || [],
            specialties: data.especialidades || [], // Alias para compatibilidad
            services: data.actividadesYCultos || [], // Para cofradías
            instruments: data.instrumentos || null,
            fotos: data.fotos || [],
            videos: data.videos || [],
            repertorio: data.repertorio || [],
            contactoPublico: data.contactoPublico || {},
            contact: {
              website: data.contactoPublico?.website || ''
            },
            redesSociales: data.redesSociales || {},
            social: {
              instagram: data.redesSociales?.instagram || '',
              facebook: data.redesSociales?.facebook || '',
              youtube: data.redesSociales?.youtube || '',
              twitter: data.redesSociales?.twitter || '',
              tiktok: data.redesSociales?.tiktok || '',
            },
            // Ensayos (solo públicos)
            ensayos: {
              publico: Array.isArray(data.ensayos) && data.ensayos.length > 0 && data.ensayos.some(e => e.publico),
              horarios: Array.isArray(data.ensayos) ? data.ensayos.filter(e => e.publico) : []
            },
            eventosPublicos: data.eventosPublicos || [],
            reviews: data.reviews || null,
            resenasRecibidas: data.resenasRecibidas || [],
            // Campos específicos de cofradía
            numeroHermanos: data.numeroHermanos,
            patrono: data.patrono,
            fechaFundacion: data.fechaFundacion,
            sedeCanonica: data.sedeCanonica,
            horariosCulto: data.horariosCulto,
            procesionesAnuales: data.procesionesAnuales,
            patrimonioArtistico: data.patrimonioArtistico,
            // Stats
            trustedBy: data._count?.resenasRecibidas || data._count?.resenasEscritas || 0,
            eventosCount: data._count?.eventosConfirmados || data._count?.eventos || 0,
            // Avales and stats will be loaded separately
            avales: [],
            stats: null,
          });

          // Load avales for bandas
          if (isBanda) {
            try {
              const [avalesRes, statsRes] = await Promise.all([
                avalesAPI.getPublicosBanda(publicEntityId),
                bandasAPI.getStats(publicEntityId),
              ]);
              setProfileData(prev => ({
                ...prev,
                avales: avalesRes.data || [],
                stats: statsRes.data || null,
              }));
            } catch (err) {
              console.error('Error loading avales/stats:', err);
            }
          }
        }
      } catch (error) {
        console.error('Error cargando perfil:', error);
        // En caso de error, profileData permanece null
        setProfileData(null);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [id, location.pathname, isBandaPath, navigate, isAuthenticated, user?.id, user?.userType]);

  if (loading) {
    return (
      <AppLayout
        headerVariant={appHeaderVariant}
        userType={user?.userType?.toLowerCase() || 'banda'}
        userName={profile?.nombre || 'Mi Perfil'}
      >
        <Container maxW="7xl" py={6}>
          <Text>Cargando...</Text>
        </Container>
      </AppLayout>
    );
  }

  if (!profileData) {
    return (
      <AppLayout
        headerVariant={appHeaderVariant}
        userType={user?.userType?.toLowerCase() || 'banda'}
        userName={profile?.nombre || 'Mi Perfil'}
      >
        <Container maxW="7xl" py={6}>
          <Alert.Root status="error" rounded="xl">
            <Alert.Indicator />
            No se encontró el perfil solicitado.
          </Alert.Root>
        </Container>
      </AppLayout>
    );
  }

  const isBanda = profileData.type === 'banda';
  const perfilVarianteCofradia = !isBanda
    ? profileData.perfilVariante || profileData.tipoEntidad?.variantePerfil || 'RELIGIOSO'
    : null;
  const labelTipoEntidadCofradia =
    profileData.tipoEntidad?.nombre || profileData.tipoGrupo || '';

  return (
    <AppLayout
      headerVariant={appHeaderVariant}
      userType={user?.userType?.toLowerCase() || 'banda'}
      userName={profile?.nombre || 'Mi Perfil'}
    >
      <Container maxW="4xl" py={6}>
        <VStack gap={6} align="stretch">
          <Button
            variant="ghost"
            size="sm"
            alignSelf="flex-start"
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else if (
                !isAuthenticated &&
                (location.pathname === '/ver-perfil-banda' || location.pathname === '/ver-perfil-cofradia')
              ) {
                navigate('/comunidad');
              } else {
                const ut = user?.userType?.toLowerCase() || 'banda';
                navigate(`/${ut}/buscar`);
              }
            }}><FaArrowLeft />{isGuestPublicProfileView ? 'Volver a Comunidad' : 'Volver a búsqueda'}</Button>

          {/* Header estilo previsualización */}
          <Box position="relative" w="full">
            {/* Imagen de fondo */}
            <Box
              w="full"
              h="320px"
              rounded="lg"
              overflow="hidden"
              backgroundImage={`url(${profileData.backgroundImage || profileData.image})`}
              backgroundSize="cover"
              backgroundPosition="center"
              position="relative"
            >
              {/* Overlay sutil para mejor contraste */}
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                bg="blackAlpha.200"
              />
            </Box>

            {/* Caja blanca con la información */}
            <Card.Root
              mt="-20px"
              rounded="2xl"
              pt={6}
              pb={6}
              px={6}
              bg="white"
              shadow="xl"
              border="1px solid"
              borderColor="gray.200"
              position="relative"
              zIndex="1"
            >
              <VStack gap={4} align="center" textAlign="center">
                {/* Información Principal */}
                <VStack gap={2}>
                  <HStack gap={4} align="center">
                    {/* Avatar a la izquierda del nombre */}
                    <Avatar.Root size="xl" border="4px solid white" bg="purple.100" shadow="lg"><Avatar.Fallback name={profileData.name}>{!isBanda && !profileData.logo ? (
                          <Icon
                            as={getCofradiaPlaceholderIcon(perfilVarianteCofradia)}
                            boxSize={10}
                          />
                        ) : undefined}</Avatar.Fallback><Avatar.Image src={profileData.logo || undefined} /></Avatar.Root>
                    <VStack gap={1} align="start">
                      <HStack gap={2}>
                        <Heading size="xl" color="gray.800">
                          {profileData.name}
                        </Heading>
                        {profileData.verified && (
                          <Tooltip content="Perfil validado" showArrow>
                            <span>
                              <Icon as={FaCheckCircle} color="blue.500" w={6} h={6} />
                            </span>
                          </Tooltip>
                        )}
                      </HStack>
                      <Text fontSize="lg" fontWeight="medium" color="gray.600">
                        {isBanda
                          ? 'Banda Musical'
                          : labelTipoEntidadCofradia || 'Entidad promotora'}
                      </Text>
                    </VStack>
                  </HStack>
                  <HStack gap={6} color="gray.500" flexWrap="wrap" justify="center">
                    <HStack gap={1}>
                      <Icon as={FaMapMarkerAlt} />
                      <Text fontSize="sm">{profileData.location}</Text>
                    </HStack>
                    {isBanda && (
                      <>
                        <HStack gap={1}>
                          <Icon as={FaUsers} />
                          <Text fontSize="sm">
                            {profileData.members} músicos
                          </Text>
                        </HStack>
                        {profileData.foundedYear ? (
                          <HStack gap={1}>
                            <Icon as={FaCalendarAlt} />
                            <Text fontSize="sm">Fundada en {profileData.foundedYear}</Text>
                          </HStack>
                        ) : null}
                      </>
                    )}
                    {!isBanda && perfilVarianteCofradia === 'RELIGIOSO' && profileData.members != null && (
                      <HStack gap={1}>
                        <Icon as={FaUsers} />
                        <Text fontSize="sm">{profileData.members} hermanos</Text>
                      </HStack>
                    )}
                    {!isBanda &&
                      perfilVarianteCofradia === 'RELIGIOSO' &&
                      profileData.foundedYear && (
                        <HStack gap={1}>
                          <Icon as={FaCalendarAlt} />
                          <Text fontSize="sm">Fundada en {profileData.foundedYear}</Text>
                        </HStack>
                      )}
                    {!isBanda &&
                      perfilVarianteCofradia === 'AYUNTAMIENTO' &&
                      profileData.habitantesMunicipio != null && (
                        <HStack gap={1}>
                          <Icon as={FaUsers} />
                          <Text fontSize="sm">
                            {profileData.habitantesMunicipio} habitantes (aprox.)
                          </Text>
                        </HStack>
                      )}
                  </HStack>
                </VStack>

                {/* Trust & Reputation Badges */}
                {isBanda && (profileData.avales?.length > 0 || profileData.stats) && (
                  <HStack gap={6} justify="center" flexWrap="wrap" py={2}>
                    {profileData.avales?.length > 0 && (
                      <HStack gap={2} bg="green.50" px={3} py={2} rounded="full">
                        <Icon as={FaHandshake} color="green.500" w={5} h={5} />
                        <Text fontSize="sm" fontWeight="bold" color="green.700">
                          {profileData.avales.length} {profileData.avales.length === 1 ? 'aval' : 'avales'}
                        </Text>
                      </HStack>
                    )}
                    {profileData.stats?.totalReviews > 0 && (
                      <HStack gap={2} bg="yellow.50" px={3} py={2} rounded="full">
                        <Icon as={FaStar} color="yellow.500" w={5} h={5} />
                        <Text fontSize="sm" fontWeight="bold" color="yellow.700">
                          {profileData.stats.averageRating}/5 ({profileData.stats.totalReviews})
                        </Text>
                      </HStack>
                    )}
                    {/* Display earned badges */}
                    {profileData.stats?.badges?.map((badge, index) => (
                      <Badge 
                        key={index} 
                        colorPalette={badge.level === 'gold' ? 'yellow' : badge.level === 'silver' ? 'gray' : 'orange'}
                        px={3} 
                        py={1} 
                        rounded="full"
                        fontSize="sm"
                      >
                        <Icon as={FaAward} mr={1} />
                        {badge.name}
                      </Badge>
                    ))}
                  </HStack>
                )}

                <HStack gap={3} pt={2} flexWrap="wrap" justify="center">
                  <ShareProfileButton shareUrl={publicShareUrl} />
                </HStack>
              </VStack>
            </Card.Root>
          </Box>

          {/* Acerca de */}
          <Card.Root rounded="2xl">
            <Card.Header>
              <Heading size="md">Acerca de</Heading>
            </Card.Header>
            <Card.Body pt={0}>
              <Text lineHeight="tall" color="gray.600">
                {profileData.longDescription || 'Sin descripción pública.'}
              </Text>
            </Card.Body>
          </Card.Root>

          {/* Cofradía / entidad — perfil religioso */}
          {!isBanda &&
            perfilVarianteCofradia === 'RELIGIOSO' &&
            (profileData.patrono ||
              profileData.sedeCanonica ||
              profileData.procesionesAnuales ||
              profileData.horariosCulto) && (
              <Card.Root rounded="2xl">
                <Card.Header>
                  <Heading size="md">
                    <Icon as={FaGavel} mr={2} color="purple.600" />
                    Información religiosa
                  </Heading>
                </Card.Header>
                <Card.Body pt={0}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
                    <VStack align="stretch" gap={3}>
                      {profileData.patrono && (
                        <HStack align="start">
                          <Icon as={FaChurch} color="purple.500" mt={1} />
                          <VStack align="start" gap={0}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                              Patrono
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {profileData.patrono}
                            </Text>
                          </VStack>
                        </HStack>
                      )}
                      {profileData.sedeCanonica && (
                        <HStack align="start">
                          <Icon as={FaChurch} color="purple.500" mt={1} />
                          <VStack align="start" gap={0}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                              Sede canónica
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {profileData.sedeCanonica}
                            </Text>
                          </VStack>
                        </HStack>
                      )}
                      {profileData.procesionesAnuales && (
                        <HStack align="start">
                          <Icon as={FaUsers} color="purple.500" mt={1} />
                          <VStack align="start" gap={0}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                              Procesiones anuales
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {profileData.procesionesAnuales}
                            </Text>
                          </VStack>
                        </HStack>
                      )}
                    </VStack>
                    {profileData.horariosCulto && (
                      <VStack align="stretch" gap={2}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          Horarios de culto
                        </Text>
                        <Text fontSize="sm" color="gray.600" whiteSpace="pre-wrap">
                          {profileData.horariosCulto}
                        </Text>
                      </VStack>
                    )}
                  </SimpleGrid>
                </Card.Body>
              </Card.Root>
            )}

          {!isBanda &&
            perfilVarianteCofradia === 'RELIGIOSO' &&
            profileData.patrimonioArtistico && (
              <Card.Root rounded="2xl">
                <Card.Header>
                  <Heading size="md">
                    <Icon as={FaHistory} mr={2} color="purple.600" />
                    Patrimonio artístico
                  </Heading>
                </Card.Header>
                <Card.Body pt={0}>
                  <Text lineHeight="tall" color="gray.600" whiteSpace="pre-wrap">
                    {profileData.patrimonioArtistico}
                  </Text>
                </Card.Body>
              </Card.Root>
            )}

          {/* Ayuntamiento / municipio */}
          {!isBanda &&
            perfilVarianteCofradia === 'AYUNTAMIENTO' &&
            (profileData.patronDelPueblo ||
              profileData.patronaDelPueblo ||
              (profileData.fiestasLocales && profileData.fiestasLocales.length > 0) ||
              profileData.habitantesMunicipio != null) && (
              <Card.Root rounded="2xl">
                <Card.Header>
                  <Heading size="md">
                    <Icon as={FaBuilding} mr={2} color="purple.600" />
                    Municipio y fiestas
                  </Heading>
                </Card.Header>
                <Card.Body pt={0}>
                  <VStack align="stretch" gap={4}>
                    {profileData.habitantesMunicipio != null && (
                      <Text fontSize="sm" color="gray.600">
                        <Text as="span" fontWeight="semibold" color="gray.700">
                          Habitantes (aprox.):{' '}
                        </Text>
                        {profileData.habitantesMunicipio}
                      </Text>
                    )}
                    {(profileData.patronDelPueblo || profileData.patronaDelPueblo) && (
                      <Text fontSize="sm" color="gray.600">
                        <Text as="span" fontWeight="semibold" color="gray.700">
                          Patronazgo:{' '}
                        </Text>
                        {[profileData.patronDelPueblo, profileData.patronaDelPueblo]
                          .filter(Boolean)
                          .join(' · ')}
                      </Text>
                    )}
                    {profileData.fiestasLocales?.length > 0 && (
                      <VStack align="stretch" gap={2} w="full">
                        {profileData.fiestasLocales.map((f, i) => {
                          const rango = formatFiestaLocalRango(f);
                          return (
                            <HStack
                              key={i}
                              align="start"
                              gap={3}
                              p={3}
                              rounded="lg"
                              bg="purple.50"
                              borderWidth="1px"
                              borderColor="purple.100"
                            >
                              <Icon as={FaCalendarAlt} color="purple.500" mt={0.5} flexShrink={0} />
                              <Box minW={0}>
                                <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                                  {f.nombre || 'Fiesta'}
                                </Text>
                                {rango ? (
                                  <Text fontSize="xs" color="gray.600" mt={0.5}>
                                    {rango}
                                  </Text>
                                ) : null}
                                {f.notas ? (
                                  <Text fontSize="xs" color="gray.600" mt={1} lineClamp={4}>
                                    {f.notas}
                                  </Text>
                                ) : null}
                              </Box>
                            </HStack>
                          );
                        })}
                      </VStack>
                    )}
                  </VStack>
                </Card.Body>
              </Card.Root>
            )}

          {/* Asociación cultural */}
          {!isBanda &&
            perfilVarianteCofradia === 'ASOCIACION_CULTURAL' &&
            (profileData.culturalAmbito ||
              profileData.culturalNumeroSocios != null ||
              profileData.culturalActividadesDescripcion) && (
              <Card.Root rounded="2xl">
                <Card.Header>
                  <Heading size="md">
                    <Icon as={FaMusic} mr={2} color="purple.600" />
                    Asociación cultural
                  </Heading>
                </Card.Header>
                <Card.Body pt={0}>
                  <VStack align="stretch" gap={3}>
                    {profileData.culturalAmbito && (
                      <Text fontSize="sm" color="gray.600">
                        <Text as="span" fontWeight="semibold" color="gray.700">
                          Ámbito:{' '}
                        </Text>
                        {profileData.culturalAmbito}
                      </Text>
                    )}
                    {profileData.culturalNumeroSocios != null && (
                      <Text fontSize="sm" color="gray.600">
                        <Text as="span" fontWeight="semibold" color="gray.700">
                          Socios (aprox.):{' '}
                        </Text>
                        {profileData.culturalNumeroSocios}
                      </Text>
                    )}
                    {profileData.culturalActividadesDescripcion && (
                      <Text lineHeight="tall" color="gray.600" whiteSpace="pre-wrap">
                        {profileData.culturalActividadesDescripcion}
                      </Text>
                    )}
                  </VStack>
                </Card.Body>
              </Card.Root>
            )}

          {/* Especialidades / actividades (banda o cofradía religiosa) */}
          {((isBanda &&
            Array.isArray(profileData.specialties) &&
            profileData.specialties.length > 0) ||
            (!isBanda &&
              perfilVarianteCofradia === 'RELIGIOSO' &&
              Array.isArray(profileData.services) &&
              profileData.services.length > 0)) && (
            <Card.Root rounded="2xl">
              <Card.Header>
                <Heading size="md">
                  {isBanda ? 'Especialidades' : 'Actividades y cultos'}
                </Heading>
              </Card.Header>
              <Card.Body pt={0}>
                <Wrap gap={2}>
                  {(isBanda ? profileData.specialties : profileData.services)?.map((item, index) => (
                    <WrapItem key={index}>
                      <Badge
                        colorPalette="purple"
                        variant="subtle"
                        px={3}
                        py={1}
                        rounded="full"
                      >
                        {item}
                      </Badge>
                    </WrapItem>
                  ))}
                </Wrap>
              </Card.Body>
            </Card.Root>
          )}

          {/* Instrumentos (solo para bandas) */}
          {isBanda && profileData.instruments && profileData.instruments.length > 0 && (
            <Card.Root rounded="2xl">
              <Card.Header>
                <Heading size="md">Instrumentos</Heading>
              </Card.Header>
              <Card.Body pt={0}>
                <Wrap gap={2}>
                  {profileData.instruments.map((instrument, index) => (
                    <WrapItem key={index}>
                      <Badge 
                        colorPalette="blue" 
                        variant="subtle" 
                        px={3} 
                        py={1}
                        rounded="full"
                      >
                        {instrument}
                      </Badge>
                    </WrapItem>
                  ))}
                </Wrap>
              </Card.Body>
            </Card.Root>
          )}

          {/* Fotos y Vídeos */}
          {(profileData.fotos && profileData.fotos.length > 0) && (
            <Card.Root rounded="2xl">
              <Card.Header>
                <Heading size="md">Galería de fotos</Heading>
              </Card.Header>
              <Card.Body pt={0}>
                <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
                  {profileData.fotos.map((foto, index) => (
                    <Image
                      key={index}
                      src={foto}
                      alt={`Foto ${index + 1}`}
                      rounded="lg"
                      objectFit="cover"
                      h="120px"
                      w="full"
                      cursor="pointer"
                      onClick={() => handleImageClick(foto)}
                      _hover={{ 
                        transform: 'scale(1.05)',
                        shadow: 'lg'
                      }}
                      transition="all 0.2s"
                    />
                  ))}
                </SimpleGrid>
              </Card.Body>
            </Card.Root>
          )}

          {/* Videos */}
          {(profileData.videos && profileData.videos.length > 0) && (
            <Card.Root rounded="2xl">
              <Card.Header>
                <Heading size="md">
                  <Icon as={FaYoutube} mr={2} color="red.500" />
                  Videos
                </Heading>
              </Card.Header>
              <Card.Body pt={0}>
                {(() => {
                  const normalVideos = profileData.videos.filter(v => !isYouTubeShort(v.url));
                  const shorts       = profileData.videos.filter(v =>  isYouTubeShort(v.url));
                  const renderCard = (video, index) => {
                    const videoId = extractYouTubeId(video.url);
                    const short = isYouTubeShort(video.url);
                    return (
                      <Box key={video.id || index} rounded="lg" overflow="hidden" shadow="md" position="relative">
                        {videoId ? (
                          <Box position="relative" pb={short ? '177.78%' : '56.25%'}>
                            <iframe
                              src={`https://www.youtube.com/embed/${videoId}`}
                              title={video.titulo}
                              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                            {short && (
                              <Badge position="absolute" top={2} left={2} colorPalette="red" fontSize="2xs" rounded="md" px={1.5} zIndex={1} pointerEvents="none">Short</Badge>
                            )}
                          </Box>
                        ) : (
                          <Box h="150px" bg="gray.100" display="flex" alignItems="center" justifyContent="center">
                            <Icon as={FaYoutube} w={12} h={12} color="gray.400" />
                          </Box>
                        )}
                        <Box p={2} bg="white">
                          <Text fontSize="sm" fontWeight="medium" isTruncated>{video.titulo}</Text>
                        </Box>
                      </Box>
                    );
                  };
                  return (
                    <VStack gap={4} align="stretch">
                      {normalVideos.length > 0 && (
                        <Box>
                          {shorts.length > 0 && <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">Vídeos</Text>}
                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                            {normalVideos.map((v, i) => renderCard(v, i))}
                          </SimpleGrid>
                        </Box>
                      )}
                      {shorts.length > 0 && (
                        <Box>
                          {normalVideos.length > 0 && <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">Shorts</Text>}
                          <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={4}>
                            {shorts.map((v, i) => renderCard(v, i))}
                          </SimpleGrid>
                        </Box>
                      )}
                    </VStack>
                  );
                })()}
              </Card.Body>
            </Card.Root>
          )}

          {/* Repertorio (solo para bandas) */}
          {isBanda && profileData.repertorio && profileData.repertorio.length > 0 && (
            <Card.Root rounded="2xl">
              <Card.Header>
                <Heading size="md">
                  <Icon as={FaMusic} mr={2} color="purple.500" />
                  Repertorio
                </Heading>
              </Card.Header>
              <Card.Body pt={0}>
                <Box
                  maxH="400px"
                  overflowY="auto"
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#CBD5E0',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#A0AEC0',
                    },
                  }}
                >
                  {profileData.repertorio.map((item, index) => {
                    // El repertorio puede venir con marcha del catálogo, con campos personalizados o directamente
                    const nombre = item.nombre || item.marcha?.nombre || item.nombreCustom || '';
                    const autor = item.autor || item.marcha?.autor || item.autorCustom || '';
                    const tipo = item.tipo || item.marcha?.tipo || item.tipoCustom || '';
                    const fromDB = !!item.marcha; // Si tiene marcha, es del catálogo
                    
                    // Debug log
                    if (index === 0) {
                      console.log('🎼 Ejemplo de item del repertorio:', { item, nombre, autor, tipo, fromDB });
                    }
                    
                    return (
                      <Flex
                        key={item.id || index}
                        p={3}
                        align="center"
                        justify="space-between"
                        bg={index % 2 === 0 ? 'white' : 'gray.50'}
                        borderBottom={index < profileData.repertorio.length - 1 ? '1px solid' : 'none'}
                        borderColor="gray.200"
                        transition="background 0.2s"
                      >
                        <HStack gap={3} flex={1}>
                          <Text 
                            fontSize="sm" 
                            fontWeight="500" 
                            color="gray.500" 
                            minW="30px"
                          >
                            {index + 1}.
                          </Text>
                          <VStack align="start" gap={0} flex={1}>
                            <HStack gap={2} flexWrap="wrap">
                              <Text fontWeight="600" fontSize="sm">
                                {nombre}
                              </Text>
                              {fromDB && (
                                <Badge colorPalette="purple" fontSize="2xs">
                                  Validado
                                </Badge>
                              )}
                            </HStack>
                            <HStack gap={2} flexWrap="wrap">
                              {autor && (
                                <Text fontSize="xs" color="gray.500">
                                  {autor}
                                </Text>
                              )}
                              {autor && tipo && (
                                <Text fontSize="xs" color="gray.400">•</Text>
                              )}
                              {tipo && (
                                <Text fontSize="xs" color="gray.500">
                                  {tipo}
                                </Text>
                              )}
                            </HStack>
                          </VStack>
                        </HStack>
                      </Flex>
                    );
                  })}
                </Box>
              </Card.Body>
            </Card.Root>
          )}

          {/* Avales de Confianza (solo para bandas) */}
          {isBanda && profileData.avales && profileData.avales.length > 0 && (
            <Card.Root rounded="2xl">
              <Card.Header>
                <HStack justify="space-between" align="start">
                  <VStack align="start" gap={1}>
                    <Heading size="md">
                      <Icon as={FaHandshake} mr={2} color="green.500" />
                      Avales de Confianza
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      Cofradías que avalan la trayectoria de esta banda
                    </Text>
                  </VStack>
                  <Badge colorPalette="green" fontSize="md" px={3} py={1} rounded="full">
                    {profileData.avales.length} {profileData.avales.length === 1 ? 'aval' : 'avales'}
                  </Badge>
                </HStack>
              </Card.Header>
              <Card.Body pt={0}>
                <VStack gap={3} align="stretch">
                  {profileData.avales.slice(0, 5).map((aval) => (
                    <Card.Root key={aval.id} variant="outline" bg="green.50">
                      <Card.Body p={4}>
                        <HStack gap={4}>
                          <Avatar.Root size="md" bg="green.100" color="green.600"><Avatar.Fallback name={aval.cofradia?.nombre}>{<Icon as={FaChurch} />}</Avatar.Fallback><Avatar.Image src={aval.cofradia?.avatar} /></Avatar.Root>
                          <VStack align="start" gap={1} flex={1}>
                            <HStack>
                              <Text fontWeight="bold" color="gray.800">
                                {aval.cofradia?.nombre}
                              </Text>
                              <Icon as={FaCheckCircle} color="green.500" w={4} h={4} />
                            </HStack>
                            {aval.texto && (
                              <Text fontSize="sm" color="gray.600" fontStyle="italic">
                                "{aval.texto}"
                              </Text>
                            )}
                            <Text fontSize="xs" color="gray.500">
                              Avalado desde {new Date(aval.acceptedAt).toLocaleDateString('es-ES', {
                                month: 'long',
                                year: 'numeric'
                              })}
                            </Text>
                          </VStack>
                          <Icon as={FaAward} w={6} h={6} color="green.500" />
                        </HStack>
                      </Card.Body>
                    </Card.Root>
                  ))}
                  
                  {profileData.avales.length > 5 && (
                    <Box textAlign="center" pt={2}>
                      <Button variant="ghost" colorPalette="green" size="sm">
                        Ver todos los avales ({profileData.avales.length})
                      </Button>
                    </Box>
                  )}
                </VStack>
              </Card.Body>
            </Card.Root>
          )}

          {/* Reseñas */}
          <Card.Root rounded="2xl">
            <Card.Header>
              <Heading size="md">
                <Icon as={FaStar} mr={2} color="yellow.500" />
                Reseñas y Valoraciones
              </Heading>
              <Text fontSize="sm" color="gray.600">
                Valoraciones tras eventos completados en la plataforma
              </Text>
            </Card.Header>
            <Card.Body pt={0}>
              <VStack gap={4} align="stretch">
                {/* Resumen de valoraciones - use stats if available */}
                {(profileData.stats || profileData.reviews) && (
                  <Card.Root variant="outline" bg="purple.50">
                    <Card.Body p={4}>
                      <HStack gap={6} justify="center">
                        <VStack gap={1}>
                          <HStack gap={1}>
                            <Text fontSize="3xl" fontWeight="bold" color="purple.600">
                              {profileData.stats?.averageRating || profileData.reviews?.rating || 0}
                            </Text>
                            <Icon as={FaStar} color="yellow.500" w={6} h={6} />
                          </HStack>
                          <Text fontSize="sm" color="gray.600">Valoración media</Text>
                        </VStack>
                        <VStack gap={1}>
                          <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                            {profileData.stats?.totalReviews || profileData.reviews?.totalReviews || 0}
                          </Text>
                          <Text fontSize="sm" color="gray.600">Reseñas totales</Text>
                        </VStack>
                      </HStack>
                    </Card.Body>
                  </Card.Root>
                )}

                {/* Reseñas individuales from API */}
                {(profileData.resenasRecibidas || []).filter(r => r.isPublished).slice(0, 3).map((resena) => {
                  // Determinar el autor correcto según el tipo de perfil
                  const autor = isBanda ? resena.autorCofradia : resena.autorBanda;
                  
                  // Calcular rating promedio
                  const ratings = isBanda 
                    ? [resena.ratingComunicacion, resena.ratingFormalidad, resena.ratingAtencion, resena.ratingGeneral].filter(Boolean)
                    : [resena.ratingPuntualidad, resena.ratingComunicacion, resena.ratingCalidadArtistica, resena.ratingGeneral].filter(Boolean);
                  const averageRating = ratings.length > 0 
                    ? Math.round(ratings.reduce((acc, val) => acc + val, 0) / ratings.length) 
                    : 0;
                  
                  return (
                    <Card.Root key={resena.id} variant="outline" bg="white">
                      <Card.Body p={4}>
                        <VStack gap={3} align="stretch">
                          {/* Header de la reseña */}
                          <HStack gap={3} justify="space-between">
                            <HStack gap={3}>
                              <Avatar.Root size="sm" bg="purple.100" color="purple.600"><Avatar.Fallback name={autor?.nombre}>{<Icon as={isBanda ? FaChurch : FaMusic} />}</Avatar.Fallback><Avatar.Image src={autor?.avatar} /></Avatar.Root>
                              <VStack align="start" gap={0}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                  {autor?.nombre || 'Anónimo'}
                                </Text>
                                <Text fontSize="xs" color="gray.600">
                                  {resena.evento?.nombre && `${resena.evento.nombre} • `}
                                  {new Date(resena.createdAt).toLocaleDateString('es-ES', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </Text>
                              </VStack>
                            </HStack>
                            
                            {/* Rating */}
                            <HStack gap={1}>
                              {[...Array(5)].map((_, i) => (
                                <Icon
                                  key={i}
                                  as={i < averageRating ? FaStar : FaRegStar}
                                  w={4}
                                  h={4}
                                  color={i < averageRating ? "yellow.500" : "gray.300"}
                                />
                              ))}
                            </HStack>
                          </HStack>
                          
                          {/* Comentario */}
                          {resena.comment && (
                            <Text fontSize="sm" color="gray.600" lineHeight="tall" fontStyle="italic">
                              "{resena.comment}"
                            </Text>
                          )}
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  );
                })}

                {/* Botón para ver más reseñas */}
                {profileData.reviews && profileData.reviews.totalReviews > 3 && (
                  <Box textAlign="center" pt={2}>
                    <Button
                      variant="ghost"
                      colorPalette="purple"
                      size="sm"
                    >
                      Ver todas las reseñas ({profileData.reviews.totalReviews})
                    </Button>
                  </Box>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Horarios de Ensayo */}
          {isBanda && profileData.ensayos && profileData.ensayos.publico && profileData.ensayos.horarios.length > 0 && (
            <Card.Root rounded="2xl">
              <Card.Header>
                <HStack>
                  <Icon as={FaClock} color="purple.500" />
                  <Heading size="md">Horarios de Ensayo</Heading>
                </HStack>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Puedes asistir como oyente en nuestros ensayos
                </Text>
              </Card.Header>
              <Card.Body pt={0}>
                <VStack gap={3} align="stretch">
                  {profileData.ensayos.horarios.map((ensayo, index) => (
                    <Box
                      key={index}
                      p={4}
                      bg="purple.50"
                      rounded="lg"
                      borderLeft="4px solid"
                      borderColor="purple.500"
                    >
                      <VStack align="start" gap={2}>
                        <HStack gap={3} w="full" justify="space-between">
                          <HStack gap={2}>
                            <Icon as={FaCalendarAlt} color="purple.600" />
                            <Text fontWeight="600" fontSize="md">
                              {ensayo.diaSemana}
                            </Text>
                          </HStack>
                          <Badge colorPalette="purple" fontSize="sm" px={3} py={1}>
                            {ensayo.horaInicio} - {ensayo.horaFin}
                          </Badge>
                        </HStack>
                        
                        {ensayo.ubicacion && (
                          <HStack gap={2}>
                            <Icon as={FaMapMarkerAlt} color="gray.500" boxSize={3} />
                            <Text fontSize="sm" color="gray.600">
                              {ensayo.ubicacion}
                            </Text>
                          </HStack>
                        )}
                        
                        {ensayo.notas && (
                          <Box
                            mt={2}
                            p={2}
                            bg="white"
                            rounded="md"
                            w="full"
                          >
                            <Text fontSize="xs" color="gray.600" fontStyle="italic">
                              {ensayo.notas}
                            </Text>
                          </Box>
                        )}
                      </VStack>
                    </Box>
                  ))}
                  
                  <Alert.Root status="info" rounded="lg" mt={2}>
                    <Alert.Indicator />
                    <VStack align="start" gap={0}>
                      <Text fontSize="sm" fontWeight="600">
                        ¿Interesado en asistir?
                      </Text>
                      <Text fontSize="xs">
                        Contacta con la banda para confirmar tu asistencia como oyente
                      </Text>
                    </VStack>
                  </Alert.Root>
                </VStack>
              </Card.Body>
            </Card.Root>
          )}

          {/* Próximos Eventos Públicos */}
          {isBanda && profileData.eventosPublicos && profileData.eventosPublicos.length > 0 && (
            <Card.Root rounded="2xl">
              <Card.Header>
                <HStack>
                  <Icon as={FaCalendarAlt} color="purple.500" />
                  <Heading size="md">Próximos Eventos</Heading>
                </HStack>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Fechas en las que esta banda tiene eventos programados
                </Text>
              </Card.Header>
              <Card.Body pt={0}>
                <VStack gap={3} align="stretch">
                  {profileData.eventosPublicos.map((evento, index) => {
                    const tipoEventoLabels = {
                      procesion: 'Procesión',
                      concierto: 'Concierto',
                      certamen: 'Certamen',
                      ensayo_especial: 'Ensayo especial',
                      otro: 'Otro evento',
                    };
                    return (
                      <Box
                        key={index}
                        p={4}
                        bg="gray.50"
                        rounded="lg"
                        borderLeft="4px solid"
                        borderColor="purple.500"
                      >
                        <HStack justify="space-between" w="full">
                          <VStack align="start" gap={1}>
                            <HStack gap={2}>
                              <Icon as={FaCalendarAlt} color="purple.600" boxSize={4} />
                              <Text fontWeight="600" fontSize="md">
                                {new Date(evento.fecha).toLocaleDateString('es-ES', {
                                  weekday: 'long',
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </Text>
                            </HStack>
                            {evento.notas && (
                              <Text fontSize="sm" color="gray.600" pl={6}>
                                {evento.notas}
                              </Text>
                            )}
                          </VStack>
                          <Badge
                            colorPalette={evento.tipoEvento === 'procesion' ? 'purple' : evento.tipoEvento === 'concierto' ? 'blue' : evento.tipoEvento === 'certamen' ? 'orange' : 'gray'}
                            fontSize="xs"
                            px={3}
                            py={1}
                            rounded="full"
                          >
                            {tipoEventoLabels[evento.tipoEvento] || evento.tipoEvento || 'Evento'}
                          </Badge>
                        </HStack>
                      </Box>
                    );
                  })}
                </VStack>
              </Card.Body>
            </Card.Root>
          )}

          {/* Información de Contacto (solo si hay web o alguna red) */}
          {(() => {
            const web = profileData.contact?.website?.trim();
            const soc = profileData.social || {};
            const hasSocial = ['instagram', 'facebook', 'youtube', 'twitter', 'tiktok'].some(
              (k) => soc[k] && String(soc[k]).trim(),
            );
            if (!web && !hasSocial) return null;
            return (
              <Card.Root rounded="2xl">
                <Card.Header>
                  <Heading size="md">Información de contacto</Heading>
                </Card.Header>
                <Card.Body pt={0}>
                  <VStack gap={6} align="center" w="full">
                    {web && (
                      <Box w="full" maxW="md">
                        <Link
                          href={normalizeWebsiteHref(web)}
                          color="purple.600"
                          _hover={{ textDecoration: 'none', opacity: 0.85 }}
                          target='_blank'
                          rel='noopener noreferrer'>
                          <HStack justify="center" gap={2} flexWrap="wrap">
                            <Icon as={FaGlobe} color="purple.500" flexShrink={0} />
                            <Text fontSize="sm" fontWeight="medium" textAlign="center" wordBreak="break-word">
                              {web}
                            </Text>
                          </HStack>
                        </Link>
                      </Box>
                    )}

                    {web && hasSocial && <Separator w="full" maxW="md" />}

                    {hasSocial && (
                      <VStack gap={3} w="full" maxW="md" align="center">
                        <Text fontSize="sm" fontWeight="600" w="full" textAlign="center">
                          Síguenos en redes sociales
                        </Text>
                        <HStack justify="center" gap={{ base: 4, md: 6 }} flexWrap="wrap" w="full">
                          {soc.instagram?.trim() && (
                            <Link
                              href={getSocialUrl('instagram', soc.instagram)}
                              _hover={{ textDecoration: 'none', opacity: 0.8 }}
                              target='_blank'
                              rel='noopener noreferrer'>
                              <HStack gap={2} cursor="pointer">
                                <Icon as={FaInstagram} color="pink.500" />
                                <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                  {soc.instagram}
                                </Text>
                              </HStack>
                            </Link>
                          )}

                          {soc.facebook?.trim() && (
                            <Link
                              href={getSocialUrl('facebook', soc.facebook)}
                              _hover={{ textDecoration: 'none', opacity: 0.8 }}
                              target='_blank'
                              rel='noopener noreferrer'>
                              <HStack gap={2} cursor="pointer">
                                <Icon as={FaFacebook} color="blue.500" />
                                <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                  {soc.facebook}
                                </Text>
                              </HStack>
                            </Link>
                          )}

                          {soc.youtube?.trim() && (
                            <Link
                              href={getSocialUrl('youtube', soc.youtube)}
                              _hover={{ textDecoration: 'none', opacity: 0.8 }}
                              target='_blank'
                              rel='noopener noreferrer'>
                              <HStack gap={2} cursor="pointer">
                                <Icon as={FaYoutube} color="red.500" />
                                <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                  {soc.youtube}
                                </Text>
                              </HStack>
                            </Link>
                          )}

                          {soc.twitter?.trim() && (
                            <Link
                              href={getSocialUrl('twitter', soc.twitter)}
                              _hover={{ textDecoration: 'none', opacity: 0.8 }}
                              target='_blank'
                              rel='noopener noreferrer'>
                              <HStack gap={2} cursor="pointer">
                                <Icon as={FaXTwitter} color="gray.800" />
                                <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                  {soc.twitter}
                                </Text>
                              </HStack>
                            </Link>
                          )}

                          {soc.tiktok?.trim() && (
                            <Link
                              href={getSocialUrl('tiktok', soc.tiktok)}
                              _hover={{ textDecoration: 'none', opacity: 0.8 }}
                              target='_blank'
                              rel='noopener noreferrer'>
                              <HStack gap={2} cursor="pointer">
                                <Icon as={FaTiktok} color="gray.800" />
                                <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                  {soc.tiktok}
                                </Text>
                              </HStack>
                            </Link>
                          )}
                        </HStack>
                      </VStack>
                    )}
                  </VStack>
                </Card.Body>
              </Card.Root>
            );
          })()}
        </VStack>
      </Container>

      {/* Modal para ver imagen en tamaño completo */}
      <Dialog.Root open={isImageModalOpen} size='full' placement='center' onOpenChange={e => {
        if (!e.open) {
          handleCloseImageModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.800" />
          <Dialog.Positioner>
            <Dialog.Content bg="transparent" boxShadow="none">
              <Dialog.CloseTrigger
                color="white"
                size="lg"
                bg="blackAlpha.600"
                _hover={{ bg: 'blackAlpha.700' }}
                rounded="full"
                m={4}
              />
              <Dialog.Body display="flex" alignItems="center" justifyContent="center" p={4}>
                {selectedImage && (
                  <Image
                    src={selectedImage}
                    alt="Imagen en tamaño completo"
                    maxH="90vh"
                    maxW="90vw"
                    objectFit="contain"
                  />
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </AppLayout>
  );
};

export default ProfileDetail;
